// src/pages/AdminAppSettings.js
//
// App Settings admin page. Top-level admin tab with two sub-nav sections:
//   General    — app-wide configuration (admin display name, future settings)
//   Danger Zone — destructive actions (moved here from top-level admin tab)

import React, { useEffect, useState } from 'react';
import { getAppConfig, updateAppConfig, updateTournament, getCurrentUser, updateOrganizerName, checkSlugAvailability, updateOrganizerSlug, updateOrganizerBranding } from '../services/api';
import AdminDangerZone    from './AdminDangerZone';
import AdminSystemLogs   from './AdminSystemLogs';
import AdminOrganizers   from './AdminOrganizers';
import AdminLegal         from './AdminLegal';
import AdminDevTools      from './AdminDevTools';
import BrandingColorPicker from '../components/BrandingColorPicker';
import { colorsAreTooSimilar } from '../constants/brandingColors';
import { pingDevTools } from '../services/api';

const SUB_TABS = [
    { id: 'general',    label: 'General' },
    { id: 'organizers', label: 'Organizers' },
    { id: 'legal',      label: 'Legal' },
    { id: 'logs',       label: 'System Logs' },
    { id: 'danger',     label: 'Danger Zone' },
];

// ---------------------------------------------------------------------------
// General settings panel
// ---------------------------------------------------------------------------

function GeneralSettings({ user = {}, onBrandingUpdated, showFeedback, activeTournament, onTournamentUpdated }) {
    // is_platform_admin only -- see the identical fix/comment in
    // AdminOrganizers.js and AdminSystemLogs.js. GET /app-config is
    // platformAdminOnly on the backend; including the legacy is_admin flag
    // here caused a doomed fetch attempt, a 403, and a "Failed to load
    // settings" toast for anyone with only that legacy flag set.
    const isPlatformAdmin = user.is_platform_admin;

    const [organizerName, setOrganizerName] = useState('');
    const [slug,          setSlug]          = useState('');
    const [savedSlug,     setSavedSlug]     = useState('');
    const [slugCheck,     setSlugCheck]     = useState(null); // { available, error } | null
    const [slugChecking,  setSlugChecking]  = useState(false);
    const [slugSaving,    setSlugSaving]    = useState(false);
    const [platformName,  setPlatformName]  = useState('');
    const [ccEmail,       setCcEmail]       = useState('');
    const [loading,       setLoading]       = useState(true);
    const [saving,        setSaving]        = useState(false);
    const [savedMsg,      setSavedMsg]      = useState('');
    const [remindersSaving, setRemindersSaving] = useState(false);
    const [canBrand,        setCanBrand]        = useState(false);
    const [brandingPrimary, setBrandingPrimary] = useState(null);
    const [brandingAccent,  setBrandingAccent]  = useState(null);
    const [brandingSaving,  setBrandingSaving]  = useState(false);
    const [brandingSavedMsg, setBrandingSavedMsg] = useState('');

    useEffect(() => {
        const fetches = [getCurrentUser()];
        if (isPlatformAdmin) fetches.push(getAppConfig());

        Promise.all(fetches)
            .then(([userRes, configRes]) => {
                setOrganizerName(userRes.data.participant.organizer_name || '');
                setSlug(userRes.data.participant.organizer_slug || '');
                setSavedSlug(userRes.data.participant.organizer_slug || '');
                setCanBrand(isPlatformAdmin || userRes.data.participant.plan_limits?.branding === true);
                setBrandingPrimary(userRes.data.participant.branding_colors?.primary || null);
                setBrandingAccent(userRes.data.participant.branding_colors?.accent || null);
                if (configRes) {
                    setPlatformName(configRes.data.admin_display_name || '');
                    setCcEmail(configRes.data.cc_email || '');
                }
            })
            .catch(() => showFeedback('Failed to load settings', 'error'))
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlatformAdmin]);

    // Debounced live availability check as the organizer types a slug.
    // Skips the check entirely if the slug matches what's already saved
    // (no point re-validating your own current value on every render) or
    // is empty.
    useEffect(() => {
        if (!slug.trim() || slug.trim() === savedSlug) {
            setSlugCheck(null);
            return;
        }
        setSlugChecking(true);
        const timeout = setTimeout(() => {
            checkSlugAvailability(slug.trim())
                .then(res => setSlugCheck(res.data))
                .catch(() => setSlugCheck({ available: false, error: 'Failed to check availability' }))
                .finally(() => setSlugChecking(false));
        }, 400);
        return () => clearTimeout(timeout);
    }, [slug, savedSlug]);

    const handleSaveSlug = async () => {
        if (!slug.trim() || slug.trim() === savedSlug) return;
        setSlugSaving(true);
        try {
            const res = await updateOrganizerSlug(slug.trim());
            setSavedSlug(res.data.slug);
            setSlug(res.data.slug);
            setSlugCheck(null);
            showFeedback('Subdomain saved');
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to save subdomain', 'error');
        } finally {
            setSlugSaving(false);
        }
    };

    const handleSaveBranding = async () => {
        if (!brandingPrimary || !brandingAccent) return;
        setBrandingSaving(true);
        try {
            const res = await updateOrganizerBranding(brandingPrimary, brandingAccent);
            setBrandingPrimary(res.data.branding_colors?.primary || null);
            setBrandingAccent(res.data.branding_colors?.accent || null);
            setBrandingSavedMsg('Saved');
            setTimeout(() => setBrandingSavedMsg(''), 2000);
            onBrandingUpdated?.(res.data.branding_colors);
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to save branding colors', 'error');
        } finally {
            setBrandingSaving(false);
        }
    };

    const handleResetBranding = async () => {
        setBrandingSaving(true);
        try {
            await updateOrganizerBranding(null, null);
            setBrandingPrimary(null);
            setBrandingAccent(null);
            showFeedback('Branding reset to platform defaults');
            onBrandingUpdated?.(null);
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to reset branding colors', 'error');
        } finally {
            setBrandingSaving(false);
        }
    };


    const handleSave = async () => {
        setSaving(true);
        setSavedMsg('');
        try {
            const updates = [];
            if (organizerName.trim()) {
                updates.push(updateOrganizerName(organizerName.trim()));
            }
            if (isPlatformAdmin) {
                updates.push(updateAppConfig({
                    admin_display_name: platformName.trim() || 'Hebert Hoops',
                    cc_email:           ccEmail.trim(),
                }));
            }
            await Promise.all(updates);
            setSavedMsg('Saved');
            setTimeout(() => setSavedMsg(''), 2000);
        } catch {
            showFeedback('Failed to save settings', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleReminders = async () => {
        if (!activeTournament || remindersSaving) return;
        setRemindersSaving(true);
        try {
            const res = await updateTournament(activeTournament.id, {
                reminders_enabled: !activeTournament.reminders_enabled,
            });
            onTournamentUpdated?.(res.data);
        } catch {
            showFeedback('Failed to update reminder setting', 'error');
        } finally {
            setRemindersSaving(false);
        }
    };

    if (loading) return <p className="text-gray-500 text-sm">Loading…</p>;

    return (
        <div className="max-w-lg">
            <h3 className="text-white font-bold text-lg mb-1">General</h3>
            <p className="text-gray-500 text-sm mb-6">
                App-wide configuration settings.
            </p>

            {/* Organizer display name */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 mb-4">
                <label className="block text-white font-bold text-sm mb-1">
                    Organizer name
                </label>
                <p className="text-gray-500 text-xs mb-3">
                    Your organization's display name — shown to participants on the
                    registration page and signed on approval, reminder, and
                    communications emails.
                </p>
                <input
                    type="text"
                    value={organizerName}
                    onChange={e => setOrganizerName(e.target.value)}
                    maxLength={100}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                    placeholder="HebertHoops - Madness Pool"
                />
            </div>

            {/* Organizer subdomain slug */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 mb-4">
                <label className="block text-white font-bold text-sm mb-1">
                    Your subdomain
                </label>
                <p className="text-gray-500 text-xs mb-3">
                    A dedicated address for your pools, e.g. <span className="text-gray-400">your-name</span>.heberthoops.com.
                    Letters, numbers, and hyphens only.
                </p>
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={slug}
                        onChange={e => setSlug(e.target.value.toLowerCase())}
                        maxLength={30}
                        className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500 font-mono"
                        placeholder="your-name"
                    />
                    <span className="text-gray-500 text-sm whitespace-nowrap">.heberthoops.com</span>
                </div>
                {slug.trim() && slug.trim() !== savedSlug && (
                    <div className="mt-2 text-xs">
                        {slugChecking ? (
                            <span className="text-gray-500">Checking availability…</span>
                        ) : slugCheck?.available ? (
                            <span className="text-green-400">✓ Available</span>
                        ) : slugCheck && !slugCheck.available ? (
                            <span className="text-red-400">{slugCheck.error}</span>
                        ) : null}
                    </div>
                )}
                <button
                    onClick={handleSaveSlug}
                    disabled={!slug.trim() || slug.trim() === savedSlug || slugSaving || slugChecking || (slugCheck && !slugCheck.available)}
                    className="mt-3 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors">
                    {slugSaving ? 'Saving…' : 'Save subdomain'}
                </button>
            </div>

            {/* Branding colors — paid tiers only (or platform admin) */}
            {canBrand && (
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 mb-4">
                    <label className="block text-white font-bold text-sm mb-1">
                        Branding colors
                    </label>
                    <p className="text-gray-500 text-xs mb-4">
                        Customize your organization name's color and your accent/button
                        color from the palette below. Pick a color for each slot, then Save.
                    </p>

                    <div className="grid sm:grid-cols-2 gap-6 mb-4">
                        <BrandingColorPicker
                            label="Primary (organization name)"
                            value={brandingPrimary}
                            onChange={setBrandingPrimary}
                            disabledHex={brandingAccent}
                        />
                        <BrandingColorPicker
                            label="Accent (buttons & highlights)"
                            value={brandingAccent}
                            onChange={setBrandingAccent}
                            disabledHex={brandingPrimary}
                        />
                    </div>

                    {brandingPrimary && brandingAccent && colorsAreTooSimilar(brandingPrimary, brandingAccent) && (
                        <p className="text-yellow-500 text-xs mb-4">
                            These two colors are quite similar — consider more contrast
                            between them for a clearer two-tone look.
                        </p>
                    )}

                    {/* Live preview */}
                    {(brandingPrimary || brandingAccent) && (
                        <div className="bg-gray-950 border border-gray-800 rounded-lg p-4 mb-4">
                            <p className="text-gray-500 text-xs mb-3">Preview</p>
                            <div
                                className="text-xl font-black tracking-tight mb-3"
                                style={{ color: brandingPrimary || '#fb923c' }}
                            >
                                {organizerName || 'Your Organization Name'}
                            </div>
                            <button
                                type="button"
                                disabled
                                className="text-sm font-bold px-4 py-2 rounded-lg"
                                style={{ backgroundColor: brandingAccent || '#fb923c', color: '#000' }}
                            >
                                Sample Button
                            </button>
                        </div>
                    )}

                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleSaveBranding}
                            disabled={!brandingPrimary || !brandingAccent || brandingSaving}
                            className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-700 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors">
                            {brandingSaving ? 'Saving…' : 'Save branding'}
                        </button>
                        {(brandingPrimary || brandingAccent) && (
                            <button
                                onClick={handleResetBranding}
                                disabled={brandingSaving}
                                className="text-gray-400 hover:text-white text-sm transition-colors">
                                Reset to platform default
                            </button>
                        )}
                        {brandingSavedMsg && (
                            <span className="text-green-400 text-sm font-bold">{brandingSavedMsg}</span>
                        )}
                    </div>
                </div>
            )}

            {/* Platform contact name — platform admins only */}
            {isPlatformAdmin && (
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 mb-4">
                    <label className="block text-white font-bold text-sm mb-1">
                        Platform contact name
                    </label>
                    <p className="text-gray-500 text-xs mb-3">
                        Name signed on platform-level emails (password reset,
                        feedback acknowledgement). Typically your personal name or
                        "Hebert Hoops Support".
                    </p>
                    <input
                        type="text"
                        value={platformName}
                        onChange={e => setPlatformName(e.target.value)}
                        maxLength={100}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                        placeholder="Darrin Hebert"
                    />
                </div>
            )}

            {/* CC email */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 mb-4">
                <label className="block text-white font-bold text-sm mb-1">
                    CC email address
                </label>
                <p className="text-gray-500 text-xs mb-3">
                    When sending communications with "CC organizer" enabled, a copy will be
                    sent to this address. Leave blank to disable CC.
                </p>
                <input
                    type="email"
                    value={ccEmail}
                    onChange={e => setCcEmail(e.target.value)}
                    maxLength={200}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                    placeholder="you@example.com"
                />
            </div>

            <div className="flex items-center gap-3 mb-4">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-bold px-5 py-2 rounded-lg text-sm transition-colors">
                    {saving ? 'Saving…' : 'Save'}
                </button>
                {savedMsg && (
                    <span className="text-green-400 text-sm font-bold">{savedMsg}</span>
                )}
            </div>

            {/* Pick deadline reminders — tournament-scoped */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-5">
                <label className="block text-white font-bold text-sm mb-1">
                    Pick deadline reminders
                </label>
                <p className="text-gray-500 text-xs mb-3">
                    {activeTournament
                        ? <>Automatic reminder emails for <span className="text-gray-300 font-bold">{activeTournament.name}</span> when a pick deadline is approaching.</>
                        : 'Select a tournament to manage its reminder emails.'}
                </p>
                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!activeTournament?.reminders_enabled}
                        onChange={handleToggleReminders}
                        disabled={!activeTournament || remindersSaving}
                        className="accent-orange-500 w-4 h-4"
                    />
                    <span className="text-sm text-gray-300">
                        {activeTournament?.reminders_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                </label>
            </div>
        </div>
    );
}

// AdminAppSettings — shell with sub-nav
// ---------------------------------------------------------------------------

function AdminAppSettings({
    user = {},
    onUserChange,
    onBrandingUpdated,
    showFeedback,
    activePool,
    activeTournament,
    onClearPool,
    onDeletePool,
    onDeletePoolParticipants,
    onDeleteTournament,
    onResetGames,
    onTournamentUpdated,
}) {
    const [subTab, setSubTab] = useState('general');
    const [devToolsEnabled, setDevToolsEnabled] = useState(false);

    // Silent check -- a 404 here just means dev tools aren't enabled in
    // this environment (the expected, correct state in production), not
    // an error worth surfacing to the user. Only bothers checking at all
    // for a platform admin, since that's the only audience this could
    // ever matter for.
    useEffect(() => {
        if (!user.is_platform_admin) return;
        pingDevTools()
            .then(() => setDevToolsEnabled(true))
            .catch(() => setDevToolsEnabled(false));
    }, [user.is_platform_admin]);

    const visibleTabs = devToolsEnabled
        ? [...SUB_TABS, { id: 'devtools', label: 'Dev Tools' }]
        : SUB_TABS;

    return (
        <div>
            {/* Sub-nav */}
            <div className="flex gap-1 mb-6 border-b border-gray-800">
                {visibleTabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setSubTab(tab.id)}
                        className={'px-4 py-2 text-sm font-bold rounded-t-lg transition-colors ' +
                            (subTab === tab.id
                                ? 'bg-gray-950 text-orange-400 border-t border-x border-gray-800'
                                : tab.id === 'danger'
                                    ? 'text-red-500 hover:text-red-400'
                                    : tab.id === 'devtools'
                                        ? 'text-yellow-500 hover:text-yellow-400'
                                        : 'text-gray-500 hover:text-gray-300')}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {subTab === 'general' && (
                <GeneralSettings
                    user={user}
                    onBrandingUpdated={onBrandingUpdated}
                    showFeedback={showFeedback}
                    activeTournament={activeTournament}
                    onTournamentUpdated={onTournamentUpdated}
                />
            )}

            {subTab === 'organizers' && (
                <AdminOrganizers
                    user={user}
                    onUserChange={onUserChange}
                    showFeedback={showFeedback}
                />
            )}

            {subTab === 'logs' && (
                <AdminSystemLogs
                    user={user}
                    showFeedback={showFeedback}
                    activeTournament={activeTournament}
                />
            )}

            {subTab === 'legal' && (
                <AdminLegal isPlatformAdmin={user.is_platform_admin} showFeedback={showFeedback} />
            )}

            {subTab === 'danger' && (
                <AdminDangerZone
                    activePool={activePool}
                    activeTournament={activeTournament}
                    onClearPool={onClearPool}
                    onDeletePool={onDeletePool}
                    onDeletePoolParticipants={onDeletePoolParticipants}
                    onDeleteTournament={onDeleteTournament}
                    onResetGames={onResetGames}
                />
            )}

            {subTab === 'devtools' && devToolsEnabled && (
                <AdminDevTools showFeedback={showFeedback} />
            )}
        </div>
    );
}

export default AdminAppSettings;